import { FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { getSizeWithinBounds } from '@folklore/size';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { ScreenElement, Empty, PlaceholderVideo } from '@micromag/core/components';
import { useScreenSize, useScreenRenderContext, useViewerNavigation, useViewerContext, useViewerWebView, usePlaybackContext, usePlaybackMediaRef, useViewerContainer } from '@micromag/core/contexts';
import { useTrackScreenMedia, useActivityDetector, useDebounce, useMediaThumbnail } from '@micromag/core/hooks';
import { isHeaderFilled, isFooterFilled, getFooterProps } from '@micromag/core/utils';
import Background from '@micromag/element-background';
import ClosedCaptions from '@micromag/element-closed-captions';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Image from '@micromag/element-image';
import Video from '@micromag/element-video';

var styles = {"container":"micromag-screen-video-container","fullscreen":"micromag-screen-video-fullscreen","image":"micromag-screen-video-image","video":"micromag-screen-video-video","placeholder":"micromag-screen-video-placeholder","inner":"micromag-screen-video-inner","empty":"micromag-screen-video-empty","emptyContainer":"micromag-screen-video-emptyContainer","content":"micromag-screen-video-content","header":"micromag-screen-video-header","bottom":"micromag-screen-video-bottom","videoContainer":"micromag-screen-video-videoContainer"};

var propTypes = {
  layout: PropTypes.oneOf(['middle', 'full']),
  video: PropTypes$1.videoElement,
  gotoNextScreenOnEnd: PropTypes.bool,
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  background: PropTypes$1.backgroundElement,
  current: PropTypes.bool,
  active: PropTypes.bool,
  preload: PropTypes.bool,
  spacing: PropTypes.number,
  mediaRef: PropTypes.func,
  className: PropTypes.string
};
var defaultProps = {
  layout: 'middle',
  video: null,
  gotoNextScreenOnEnd: false,
  header: null,
  footer: null,
  background: null,
  current: true,
  active: true,
  preload: true,
  spacing: 20,
  mediaRef: null,
  className: null
};
var VideoScreen = function VideoScreen(_ref) {
  var layout = _ref.layout,
    video = _ref.video,
    gotoNextScreenOnEnd = _ref.gotoNextScreenOnEnd,
    header = _ref.header,
    footer = _ref.footer,
    background = _ref.background,
    current = _ref.current;
    _ref.active;
    var preload = _ref.preload,
    spacing = _ref.spacing,
    customMediaRef = _ref.mediaRef,
    className = _ref.className;
  var trackScreenMedia = useTrackScreenMedia('video');
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit,
    isStatic = _useScreenRenderConte.isStatic,
    isCapture = _useScreenRenderConte.isCapture;
  var _useViewerNavigation = useViewerNavigation(),
    gotoNextScreen = _useViewerNavigation.gotoNextScreen;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var mediaShouldLoad = current || preload;
  var shouldGotoNextScreenOnEnd = gotoNextScreenOnEnd && isView && current;
  var _ref2 = video || {},
    _ref2$autoPlay = _ref2.autoPlay,
    autoPlay = _ref2$autoPlay === void 0 ? true : _ref2$autoPlay,
    _ref2$loop = _ref2.loop,
    loop = _ref2$loop === void 0 ? false : _ref2$loop,
    _ref2$media = _ref2.media,
    videoMedia = _ref2$media === void 0 ? null : _ref2$media,
    _ref2$thumbnail = _ref2.thumbnail,
    thumbnail = _ref2$thumbnail === void 0 ? null : _ref2$thumbnail,
    _ref2$captions = _ref2.captions,
    captions = _ref2$captions === void 0 ? null : _ref2$captions,
    _ref2$closedCaptions = _ref2.closedCaptions,
    closedCaptions = _ref2$closedCaptions === void 0 ? null : _ref2$closedCaptions,
    _ref2$withSeekBar = _ref2.withSeekBar,
    withSeekBar = _ref2$withSeekBar === void 0 ? false : _ref2$withSeekBar,
    _ref2$withControls = _ref2.withControls,
    withControls = _ref2$withControls === void 0 ? false : _ref2$withControls,
    _ref2$color = _ref2.color,
    color = _ref2$color === void 0 ? null : _ref2$color,
    _ref2$progressColor = _ref2.progressColor,
    progressColor = _ref2$progressColor === void 0 ? null : _ref2$progressColor;
  var _usePlaybackContext = usePlaybackContext(),
    playing = _usePlaybackContext.playing,
    muted = _usePlaybackContext.muted,
    setControls = _usePlaybackContext.setControls,
    setControlsSuggestPlay = _usePlaybackContext.setControlsSuggestPlay,
    setControlsTheme = _usePlaybackContext.setControlsTheme,
    setPlaying = _usePlaybackContext.setPlaying,
    controlsVisible = _usePlaybackContext.controlsVisible,
    showControls = _usePlaybackContext.showControls,
    hideControls = _usePlaybackContext.hideControls,
    currentQualityLevel = _usePlaybackContext.currentQualityLevel,
    setCurrentQualityLevel = _usePlaybackContext.setCurrentQualityLevel;
  var mediaRef = usePlaybackMediaRef(current);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasPlayed = _useState2[0],
    setHasPlayed = _useState2[1];
  var backgroundPlaying = current && (isView || isEdit);
  var videoPlaying = current && (isView || isEdit) && playing;
  var shouldDisplayPoster = isPreview || isCapture;
  useEffect(function () {
    if (!current) {
      return function () {};
    }
    setControlsTheme({
      seekBarOnly: withSeekBar && !withControls,
      color: color,
      progressColor: progressColor
    });
    if (withControls || withSeekBar) {
      setControls(true);
    } else {
      setControls(false);
    }
    return function () {
      if (withControls || withSeekBar) {
        setControls(false);
      }
    };
  }, [current, withControls, setControls, withSeekBar, color, progressColor]);
  useEffect(function () {
    if (customMediaRef !== null) {
      customMediaRef(mediaRef.current);
    }
  }, [mediaRef.current]);
  useEffect(function () {
    if (current && autoPlay) {
      setPlaying(true);
    }
  }, [current, autoPlay]);
  var viewerContainer = useViewerContainer();
  var _useActivityDetector = useActivityDetector({
      element: viewerContainer,
      disabled: !isView,
      timeout: 2000
    }),
    activityDetected = _useActivityDetector.detected;
  var toggleControlsVisibility = useCallback(function () {
    if (activityDetected) {
      showControls();
    } else {
      hideControls();
    }
  }, [activityDetected, showControls, hideControls]);
  useDebounce(toggleControlsVisibility, activityDetected, 1000);
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    currentTime = _useState4[0],
    setCurrentTime = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    duration = _useState6[0],
    setDuration = _useState6[1];
  var onTimeUpdate = useCallback(function () {
    var time = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    if (time !== null && typeof time.currentTarget !== 'undefined') {
      var _ref3 = time.currentTarget || {},
        _ref3$currentTime = _ref3.currentTime,
        targetTime = _ref3$currentTime === void 0 ? 0 : _ref3$currentTime;
      setCurrentTime(targetTime);
    } else {
      setCurrentTime(0);
    }
  }, [duration, setCurrentTime]);
  var onProgressStep = useCallback(function (step, meta) {
    trackScreenMedia(videoMedia, "progress_".concat(Math.round(step * 100, 10), "%"), meta);
  }, [trackScreenMedia, videoMedia]);
  var onDurationChange = useCallback(function (dur) {
    setDuration(dur);
  }, [setDuration]);
  var onPlay = useCallback(function (_ref4) {
    var initial = _ref4.initial;
    if (!hasPlayed) {
      setHasPlayed(true);
    }
    trackScreenMedia(videoMedia, initial ? 'play' : 'resume');
  }, [trackScreenMedia, videoMedia]);
  var onPause = useCallback(function (_ref5) {
    var midway = _ref5.midway;
    trackScreenMedia(videoMedia, midway ? 'pause' : 'ended');
  }, [trackScreenMedia, videoMedia]);
  var onSeeked = useCallback(function (time) {
    if (time > 0) {
      trackScreenMedia(videoMedia, 'seek', {
        currentTime: time
      });
    }
  }, [trackScreenMedia, videoMedia]);
  var onEnded = useCallback(function () {
    if (current && !loop) {
      setPlaying(false);
    }
    if (current && shouldGotoNextScreenOnEnd) {
      gotoNextScreen();
    }
  }, [loop, current, shouldGotoNextScreenOnEnd, gotoNextScreen]);
  var fullscreen = layout === 'full';
  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview
  });
  var hasVideo = video !== null;
  var _useState7 = useState(hasVideo),
    _useState8 = _slicedToArray(_useState7, 2),
    ready = _useState8[0],
    setReady = _useState8[1];
  var finalVideo = useMemo(function () {
    return hasVideo ? _objectSpread(_objectSpread({}, video), {}, {
      autoPlay: !isPreview && !isStatic && !isCapture && autoPlay && current
    }) : null;
  }, [hasVideo, video, isPreview, isStatic, isCapture, autoPlay, current]);
  var _ref6 = videoMedia || {},
    _ref6$metadata = _ref6.metadata,
    videoMetadata = _ref6$metadata === void 0 ? null : _ref6$metadata,
    _ref6$url = _ref6.url,
    videoUrl = _ref6$url === void 0 ? null : _ref6$url;
  var finalThumbnail = useMediaThumbnail(videoMedia, thumbnail);
  var hasVideoUrl = videoUrl !== null;
  var _ref7 = videoMetadata || {},
    _ref7$width = _ref7.width,
    videoWidth = _ref7$width === void 0 ? 0 : _ref7$width,
    _ref7$height = _ref7.height,
    videoHeight = _ref7$height === void 0 ? 0 : _ref7$height;
  var _getSizeWithinBounds = getSizeWithinBounds(videoWidth, videoHeight, width, height, {
      cover: fullscreen
    }),
    resizedVideoWidth = _getSizeWithinBounds.width,
    resizedVideoHeight = _getSizeWithinBounds.height;
  var resizedVideoLeft = -(resizedVideoWidth - width) / 2;
  var resizedVideoTop = -(resizedVideoHeight - height) / 2;
  var placeholderProps = fullscreen ? {
    width: '100%',
    height: '100%'
  } : {
    width: '100%'
  };
  useEffect(function () {
    setReady(!hasVideoUrl);
  }, [videoUrl, hasVideoUrl, setReady]);
  var onVideoReady = useCallback(function () {
    setReady(true);
  }, [setReady]);

  // const onSuspended = useCallback(() => {
  //     if (playing && current) {
  //         setPlaying(false);
  //     }
  // }, [current, playing, setPlaying]);

  var onPlayError = useCallback(function () {
    if (isView && playing && current && hasVideoUrl && autoPlay) {
      setPlaying(false);
      setControlsSuggestPlay(true);
    }
  }, [isView, current, playing, hasVideoUrl, autoPlay, setPlaying, setControlsSuggestPlay]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty({}, className, className !== null), styles.fullscreen, fullscreen)]),
    "data-screen-ready": isStatic || isCapture || ready
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.inner
  }, !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    key: "header",
    className: styles.header,
    style: {
      paddingTop: spacing / 2,
      paddingLeft: spacing,
      paddingRight: spacing,
      transform: !isPreview ? "translate(0, ".concat(viewerTopHeight, "px)") : null
    }
  }, /*#__PURE__*/React.createElement(Header, Object.assign({
    fade: current && !controlsVisible
  }, header))) : null, /*#__PURE__*/React.createElement(ScreenElement, {
    key: "video",
    placeholder: /*#__PURE__*/React.createElement(PlaceholderVideo, Object.assign({
      className: styles.placeholder
    }, placeholderProps)),
    empty: /*#__PURE__*/React.createElement("div", {
      className: styles.emptyContainer
    }, /*#__PURE__*/React.createElement(Empty, {
      className: styles.empty
    }, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "3ext9Q",
      defaultMessage: [{
        "type": 0,
        "value": "Video"
      }]
    }))),
    isEmpty: !hasVideoUrl
  }, hasVideoUrl ? /*#__PURE__*/React.createElement("div", {
    className: styles.videoContainer,
    style: {
      width: resizedVideoWidth,
      height: resizedVideoHeight,
      left: resizedVideoLeft,
      top: resizedVideoTop
    }
  }, shouldDisplayPoster ? /*#__PURE__*/React.createElement(Image, {
    className: styles.image,
    media: finalThumbnail,
    width: resizedVideoWidth,
    height: resizedVideoHeight,
    objectFit: {
      fit: 'cover'
    },
    resolution: resolution,
    shouldLoad: mediaShouldLoad
  }) : /*#__PURE__*/React.createElement(Video, Object.assign({}, finalVideo, {
    width: resizedVideoWidth,
    height: resizedVideoHeight,
    paused: !videoPlaying,
    muted: muted,
    mediaRef: mediaRef,
    className: styles.video,
    onReady: onVideoReady,
    onPlay: onPlay,
    onPause: onPause,
    onTimeUpdate: onTimeUpdate,
    onProgressStep: onProgressStep,
    onDurationChange: onDurationChange,
    onSeeked: onSeeked,
    onEnded: onEnded
    // onSuspended={onSuspended}
    ,
    onPlayError: onPlayError,
    disablePictureInPicture: true,
    focusable: current && isView,
    shouldLoad: mediaShouldLoad,
    qualityStartLevel: currentQualityLevel,
    onQualityLevelChange: setCurrentQualityLevel
  }))) : null), !isPlaceholder ? /*#__PURE__*/React.createElement("div", {
    key: "bottom-content",
    className: styles.bottom,
    style: {
      transform: current && !isPreview ? "translate3d(0, -".concat(viewerBottomHeight, "px, 0)") : null,
      paddingLeft: Math.max(spacing / 2, viewerBottomSidesWidth),
      paddingRight: Math.max(spacing / 2, viewerBottomSidesWidth),
      paddingBottom: spacing / 2,
      paddingTop: 0
    }
  }, (closedCaptions !== null || captions !== null) && !isPreview && !isCapture && !isStatic ? /*#__PURE__*/React.createElement(ClosedCaptions, Object.assign({
    className: styles.closedCaptions,
    media: closedCaptions // BW Compat
  }, captions, {
    currentTime: currentTime
  })) : null, hasFooter ? /*#__PURE__*/React.createElement(Footer, footerProps) : null) : null)), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: mediaShouldLoad,
    withoutVideo: isPreview
  }) : null);
};
VideoScreen.propTypes = propTypes;
VideoScreen.defaultProps = defaultProps;

var definition = {
  id: 'video',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "+9akmg",
      defaultMessage: [{
        "type": 0,
        "value": "Audio and Video"
      }]
    }),
    order: 5
  },
  title: defineMessage({
    id: "UBXIYP",
    defaultMessage: [{
      "type": 0,
      "value": "Video"
    }]
  }),
  component: VideoScreen,
  layouts: ['middle', 'full'],
  // transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'middle',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'video',
    type: 'video-element',
    theme: {
      color: 'primary'
    },
    defaultValue: {
      autoPlay: true
    },
    label: defineMessage({
      id: "tvl2Zc",
      defaultMessage: [{
        "type": 0,
        "value": "Video"
      }]
    })
  }, {
    name: 'gotoNextScreenOnEnd',
    type: 'toggle',
    defaultValue: false,
    label: defineMessage({
      id: "n8zmLY",
      defaultMessage: [{
        "type": 0,
        "value": "Go to next screen on end"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
};

export { VideoScreen, definition as default };
